document.addEventListener('alpine:init', () => {
  Alpine.data('CartItems', () => ({
    itemsRoot: null,
    loading: null,

    // --- Free sample config & helpers (NEW) ---
    FREE_SAMPLE_HANDLE: 'free-sample',

    EXCLUDED_PRODUCT_IDS: [],            // e.g. [1234567890]
    EXCLUDED_HANDLES: ['gift-card'],     // handles to exclude
    EXCLUDED_TYPES: ['Gift Card'],       // product_type values
    EXCLUDED_VENDORS: [],                // vendor names
    EXCLUDED_SKUS: [],                   // SKU strings
    EXCLUDED_TAGS: ['no-sample'],        // product tags to exclude

    _productTagsCache: new Map(),        // product_id -> Set(tags)

    _getHandleFromUrl(url) {
      // cart line items typically have url like '/products/handle?variant=...'
      if (!url) return null;
      const idx = url.indexOf('/products/');
      if (idx === -1) return null;
      const after = url.slice(idx + '/products/'.length);
      const handle = after.split('?')[0].split('#')[0].split('/')[0];
      return handle || null;
    },

    async _getProductTagsForItem(item) {
      if (!item || !this.EXCLUDED_TAGS.length) return new Set();

      if (this._productTagsCache.has(item.product_id)) {
        return this._productTagsCache.get(item.product_id);
      }

      const handle =
        item.handle /* some themes surface this */ ||
        this._getHandleFromUrl(item.url);

      if (!handle) {
        const empty = new Set();
        this._productTagsCache.set(item.product_id, empty);
        return empty;
      }

      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) throw new Error('product fetch failed');
        const product = await res.json();

        let tagsArr = [];
        if (Array.isArray(product.tags)) {
          tagsArr = product.tags;
        } else if (typeof product.tags === 'string') {
          tagsArr = product.tags.split(',').map(t => t.trim()).filter(Boolean);
        }

        const tagSet = new Set(tagsArr);
        this._productTagsCache.set(item.product_id, tagSet);
        return tagSet;
      } catch (err) {
        console.warn('Unable to load product tags for', handle, err);
        const empty = new Set();
        this._productTagsCache.set(item.product_id, empty);
        return empty;
      }
    },

    async _isExcluded(item) {
      if (!item) return false;

      // Per-line opt-out property
      if (
        item.properties &&
        (item.properties._no_free_sample === true ||
          item.properties._no_free_sample === 'true' ||
          item.properties._no_free_sample === '1')
      ) {
        return true;
      }

      if (this.EXCLUDED_PRODUCT_IDS.includes(item.product_id)) return true;
      if (item.handle && this.EXCLUDED_HANDLES.includes(item.handle)) return true;
      if (item.product_type && this.EXCLUDED_TYPES.includes(item.product_type)) return true;
      if (item.vendor && this.EXCLUDED_VENDORS.includes(item.vendor)) return true;
      if (item.sku && this.EXCLUDED_SKUS.includes(item.sku)) return true;

      // Tag exclusion
      if (this.EXCLUDED_TAGS.length) {
        const tagSet = await this._getProductTagsForItem(item);
        for (const tag of this.EXCLUDED_TAGS) {
          if (tagSet.has(tag)) return true;
        }
      }

      return false;
    },
    // --- end config & helpers ---

    async init() {
      this.itemsRoot = this.$root;

      window.addEventListener(
        'baseline:cart:afteradditem',
        this.onCartQuantityChange.bind(this)
      );

      window.addEventListener(
        'baseline:cart:cartqtychange',
        this.onCartQuantityChange.bind(this)
      );

      await this.checkAndAddFreeSample();
    },

    async onCartQuantityChange(e) {
      Alpine.morph(
        this.itemsRoot,
        querySelectorInHTMLString(
          '[data-cart-items]',
          e.detail.response.sections['cart-items']
        ).outerHTML
      );

      this.$nextTick(() => {
        this.itemsRoot.querySelectorAll('input').forEach((inputEl) => {
          inputEl.value = inputEl.getAttribute('value');
          inputEl.dispatchEvent(new Event('input'));
        });
      });

      this.updateLiveRegion(
        parseDOMFromString(e.detail.response.sections['cart-live-region'])
          .firstElementChild.textContent
      );

      if (e.detail.originalTarget) {
        this.$nextTick(() => {
          if (!this.itemsRoot.contains(e.detail.originalTarget)) {
            let focusRoot;

            if (this.itemsRoot.closest('[role="dialog"]')) {
              focusRoot = this.itemsRoot.closest('[role="dialog"]').parentNode;
            } else {
              focusRoot = this.itemsRoot;
            }

            this.$focus.within(focusRoot).first();
          }
        });
      }

      await this.checkAndAddFreeSample();
    },

    updateLiveRegion(liveRegionText) {
      if (!liveRegionText) return;

      const cartStatus = document.getElementById('cart-live-region-text');

      cartStatus.textContent = liveRegionText;
      cartStatus.setAttribute('aria-hidden', false);

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1000);
    },

    async checkAndAddFreeSample() {
      const productHandle = this.FREE_SAMPLE_HANDLE;

      try {
        const prodRes = await fetch(`/products/${productHandle}.js`);
        const product = await prodRes.json();
        if (!product || !product.variants || !product.variants.length) return;

        const availableVariants = product.variants.filter(v => v.available);
        if (!availableVariants.length) return;

        const selectedVariant = availableVariants[0];
        const sampleProductId = product.id;

        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();

        const sampleItems = cart.items.filter(item => item.product_id === sampleProductId);
        const sampleAlreadyInCart = sampleItems.length > 0;

        // Any non-sample item that is NOT excluded?
        let hasEligibleItems = false;
        for (const item of cart.items) {
          const isSample = item.product_id === sampleProductId;
          if (isSample) continue;
          const excluded = await this._isExcluded(item);
          if (!excluded) {
            hasEligibleItems = true;
            break;
          }
        }

        if (!sampleAlreadyInCart && hasEligibleItems) {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: selectedVariant.id,
              quantity: 1,
            }),
          });

          const sectionsRes = await fetch(
            `${window.location.pathname}?sections=cart-items,cart-footer,cart-item-count,cart-live-region`
          );
          const sections = await sectionsRes.json();
          document.body.dispatchEvent(
            new CustomEvent('baseline:cart:cartqtychange', {
              bubbles: true,
              detail: { response: { sections }, originalTarget: null },
            })
          );
        } else if (sampleAlreadyInCart && !hasEligibleItems) {
          // Remove sample if only excluded (or no) items remain
          const sampleItem = sampleItems[0];
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: sampleItem.key,
              quantity: 0,
            }),
          });

          const sectionsRes = await fetch(
            `${window.location.pathname}?sections=cart-items,cart-footer,cart-item-count,cart-live-region`
          );
          const sections = await sectionsRes.json();
          document.body.dispatchEvent(
            new CustomEvent('baseline:cart:cartqtychange', {
              bubbles: true,
              detail: { response: { sections }, originalTarget: null },
            })
          );
        }
      } catch (e) {
        console.error('checkAndAddFreeSample error', e);
      }
    },
  }));

  Alpine.data('CartItem', (key) => ({
    quantity: null,
    previousQuantity: null,
    key,
    locked: false,
    errorMessage: null,

    async itemQuantityChange() {
      if (this.locked || this.loading) return;

      if (this.$refs.quantityInput?.hasAttribute('data-last-value')) {
        if (
          this.quantity === Number(this.$refs.quantityInput.dataset.lastValue)
        ) {
          return;
        }
      }

      this.locked = true;
      this.loading = true;

      const request = {
        ...fetchConfigDefaults('application/javascript'),
        body: JSON.stringify({
          id: this.key,
          quantity: this.quantity,
          sections: 'cart-items,cart-footer,cart-item-count,cart-live-region',
          sections_url: window.location.pathname,
        }),
      };

      try {
        const response = await fetch(theme.routes.cart_change_url, request);
        const data = await response.json();

        if (data.status === 422) {
          this.errorMessage = data.message;
          this.quantity = this.previousQuantity;
        } else {
          this.errorMessage = null;

          document.body.dispatchEvent(
            new CustomEvent('baseline:cart:cartqtychange', {
              bubbles: true,
              detail: { response: data, originalTarget: this.$root },
            })
          );
        }
      } catch (e) {
        console.error(e);
        document.getElementById('cart-errors').textContent =
          theme.strings.cartError;
      } finally {
        this.locked = false;
        this.loading = false;
      }
    },

    removeItem() {
      const visibleQtyControl = Array.from(
        this.$root.querySelectorAll('[x-data="CartItemQuantity"]')
      ).filter((el) => el.offsetParent)[0];

      visibleQtyControl.dispatchEvent(new CustomEvent('remove'));
    },
  }));

  Alpine.data('CartItemQuantity', () => ({
    init() {
      this.$root.addEventListener('remove', () => {
        this.remove();
      });
    },

    remove() {
      this.adjustQuantity(() => {
        this.$refs.quantityInput.value = 0;
      });
    },

    ...coreQuantity,
  }));
});
